/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AppBar, Button, Checkbox, Grid, IconButton, Radio, Tab, TableCell, TableRow, Tabs, TextField, Typography, Dialog, DialogActions, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { escapeRegExp } from 'lodash';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useStyles } from '../styles';
import { CheckboxIcon, CheckIcon, CustomDialogTitle, DateRangeSelect, FieldLabel, InfoIcon, LightTooltip, CustomTable, RadioBtnCheckedIcon, SearchField } from '../../../../components';
import { CalculationPeriod, CalculationPeriodOptions, Campaign, CampaignClient, EditCampaign, PeriodType } from '../../CashbackModel';
import { useCommonStyles } from '../../../../styles';
import { useApiError, useLoading } from '../../../../providers';
import { getClientsForCampaign } from '../../CashbackService';
import { a11yProps } from '../../../../utils';

interface Props {
  partnerId: number;
  campaign?: Campaign;
  translation: any;
  open: boolean;
  handleCancel: () => void;
  onSave: (campaign: EditCampaign) => void;
}

export const AddEditCampaignDialog: FC<Props> = ({ partnerId, campaign, translation, open, handleCancel, onSave }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();
  const currentDate = useMemo(() => moment().toDate(), []);
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().toDate(), moment().toDate()]);
  const [name, setName] = useState<string>('');
  const [periodType, setPeriodType] = useState<PeriodType>(PeriodType.Permanent);
  const [calculationPeriod, setCalculationPeriod] = useState<CalculationPeriod>(CalculationPeriod.Monthly);
  const [clients, setClients] = useState<CampaignClient[]>([]);
  const [filteredClients, setFilteredClients] = useState<CampaignClient[]>([]);
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const columns = useMemo(() => [translation.text_170, translation.trading_account_id, translation.campaign], [translation]);
  const [showError, setShowError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(2);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setPeriodType(campaign.periodType === 'Permanent' ? PeriodType.Permanent : PeriodType.DateRange);
      setSelectedDateRange([campaign.startDate ? campaign.startDate : currentDate, campaign.endDate ? campaign.endDate : currentDate]);
      setCalculationPeriod(campaign.calculationPeriod === 'Daily' ? CalculationPeriod.Daily : campaign.calculationPeriod === 'Monthly' ? CalculationPeriod.Monthly : CalculationPeriod.Weekly);
      setSelectedClients(campaign.campaignClients ? campaign.campaignClients.map((c) => c.clientLogin) : []);
    }
  }, [campaign, currentDate]);

  const getClients = useCallback(async (partnerId: number) => {
    setIsLoading(true);
    try {
      const response = await getClientsForCampaign(partnerId);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getClients(partnerId);
    })();
  }, []);

  const selectAllClients = useCallback(() => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      const selectedIds = clients.map(({ id }) => id);
      setSelectedClients(selectedIds);
    }
  }, [selectedClients, clients]);

  const changeSelectedClients = useCallback(
    (client: CampaignClient) => {
      let clientIds = [...selectedClients];
      if (selectedClients.find((selected) => selected === client.id)) {
        clientIds = clientIds.filter((selected) => selected !== client.id);
      } else {
        clientIds.push(client.id);
      }
      setSelectedClients(clientIds);
    },
    [selectedClients],
  );

  const renderHeader = useMemo((): any[] => {
    const result: any[] = [];
    result.push(
      <TableCell padding="checkbox">
        <Checkbox checked={selectedClients.length === clients.length} checkedIcon={<CheckboxIcon />} onClick={selectAllClients} />
      </TableCell>,
    );
    columns.forEach((columnName) => {
      result.push(
        <TableCell key={columnName} align="center">
          {columnName}
        </TableCell>,
      );
    });
    return result;
  }, [columns, selectedClients, classes, clients, selectAllClients]);

  const renderRows = useCallback((): any[] => {
    const result: any[] = [];
    filteredClients.forEach((row) => {
      result.push(
        <TableRow className={commonClasses.tableRow} key={Math.random()}>
          <TableCell padding="checkbox">
            <Checkbox checked={selectedClients.indexOf(row.id) > -1} checkedIcon={<CheckboxIcon />} onClick={() => changeSelectedClients(row)} />
          </TableCell>
          <TableCell align="center">{row.name}</TableCell>
          <TableCell align="center">{row.id}</TableCell>
          <TableCell align="center">{row.currentCampaignName === 'Default' ? translation.default : row.currentCampaignName}</TableCell>
        </TableRow>,
      );
    });
    return result;
  }, [filteredClients, selectedClients, changeSelectedClients, classes, commonClasses]);

  const requestSearch = (searchValue: string) => {
    if (searchValue !== '') {
      const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
      const filteredRows = clients.filter((client) => searchRegex.test(client.name.toString()) || searchRegex.test(client.id.toString()));
      setFilteredClients(filteredRows);
    } else {
      setFilteredClients(clients);
    }
  };

  const handleSave = (): void => {
    if (name === undefined || name === '') {
      setShowError(true);
    } else if (moment(selectedDateRange[0]).format('DD MM YYYY') <= moment(selectedDateRange[1]).format('DD MM YYYY')) {
      const newCampaign = {
        name: name || '',
        partnerId,
        periodType,
        startDate: periodType === PeriodType.Permanent ? null : selectedDateRange[0],
        endDate: periodType === PeriodType.Permanent ? null : selectedDateRange[1],
        calculationPeriod,
        clients: selectedClients,
      };
      onSave(newCampaign);
    }
  };

  const handleTabChange = useCallback((event: any, newValue: number) => {
    setActiveTab(newValue);
    setCalculationPeriod(CalculationPeriodOptions[newValue].value);
  }, []);

  return (
    <Dialog onClose={handleCancel} open={open} classes={{ paper: classes.dialog }} disableEnforceFocus>
      <CustomDialogTitle title={campaign ? translation.edit_campaign : translation.new_campaign} onClose={handleCancel} />
      <Grid container direction="column" alignItems="flex-start" className={commonClasses.dialogContent}>
        <Grid container>
          <Grid item xs={12}>
            <FieldLabel label={translation.campaign_name} isRequired />
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              type="text"
              className={classes.inputField}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setShowError(false);
              }}
              placeholder={translation.specify}
              error={showError}
              helperText={showError ? translation.text_189 : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldLabel label={translation.cashback_calculation_period} isRequired />
            <AppBar position="static" className={commonClasses.tabs}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                {/* <Tab label={translation.text_1269} value={0} {...a11yProps(0)} /> */}
                {/* <Tab label={translation.weekly} value={1} {...a11yProps(1)} /> */}
                <Tab label={translation.text_1270} value={2} {...a11yProps(2)} />
              </Tabs>
            </AppBar>
          </Grid>
          <Grid item xs={12} className={classes.subTitleRow}>
            <p className={classes.subTitle}>{translation.timeframe}</p>
          </Grid>
          <Grid item xs={12} className={classes.margin}>
            <List dense>
              <ListItem key={PeriodType.DateRange}>
                <ListItemIcon>
                  <Radio checked={periodType === PeriodType.DateRange} checkedIcon={<RadioBtnCheckedIcon />} onChange={() => setPeriodType(PeriodType.DateRange)} />
                </ListItemIcon>
                <ListItemText>
                  <FieldLabel label={translation.campaign_active_period} isRequired>
                    <LightTooltip title={translation.campaign_active_period_tooltip} arrow placement="right-start">
                      <IconButton size="small" style={{ marginTop: '-10px' }}>
                        <InfoIcon className={classes.infoIcon} />
                      </IconButton>
                    </LightTooltip>
                  </FieldLabel>
                  <DateRangeSelect minDate={new Date()} defaultValue={selectedDateRange} onRangeChange={(value) => setSelectedDateRange(value)} />
                </ListItemText>
              </ListItem>
              <ListItem key={PeriodType.Permanent}>
                <ListItemIcon>
                  <Radio checked={periodType === PeriodType.Permanent} checkedIcon={<RadioBtnCheckedIcon />} onChange={() => setPeriodType(PeriodType.Permanent)} />
                </ListItemIcon>
                <ListItemText>{translation.campaign_period_permanent}</ListItemText>
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row" justify="space-between" className={classes.clientsTitleRow}>
              <Grid item xs={6}>
                <Typography className={classes.clientTitle}>
                  {translation.add_clients} ({clients.length})
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Grid container direction="row" justify="flex-end">
                  <SearchField onSearch={requestSearch} showReset placeholder={translation.search_for_client} />
                </Grid>
              </Grid>
            </Grid>
            <CustomTable headerCells={renderHeader} rows={renderRows()} rowsCountNumber={5} columnsNumber={columns.length + 1} emptyDataMessage={translation.no_clients} />
          </Grid>
        </Grid>
      </Grid>
      <DialogActions className={classes.dialogAction}>
        <Button onClick={handleSave} startIcon={campaign ? <CheckIcon /> : ''} className={`${commonClasses.activeDialogBtn} ${classes.btn}`}>
          {campaign ? translation.text_10 : translation.create_campaign}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
